import React, { useEffect, useMemo, useState } from 'react';
import { getRouteMenus } from './VerticalMenuUtils';
import clsx from 'clsx';
import defaultConfig from '@nxsys/constants/defaultConfig';
import { useSidebarContext } from '@nxsys/context/AppContextProvider/SidebarContextProvider';
import { MenuStyle } from '@nxsys/constants/AppEnums';
import { StyledVerticalNav } from './index.styled';
import { useLocation } from 'react-router-dom';
import { RouterConfigData } from '@nxsys/types/models/Apps';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';

// Collect all menu item IDs and URL→ID mappings from route config
function collectMenuData(routes: RouterConfigData[]): { ids: string[]; urlToId: Record<string, string> } {
  const ids: string[] = [];
  const urlToId: Record<string, string> = {};
  for (const route of routes) {
    if (route.id) ids.push(route.id);
    if (route.id && route.url) urlToId[route.url.replace(/^\//, '')] = route.id;
    if (route.children) {
      const child = collectMenuData(route.children);
      ids.push(...child.ids);
      Object.assign(urlToId, child.urlToId);
    }
  }
  return { ids, urlToId };
}

// Find parent collapse ID that contains the selected child
function findParentId(routes: RouterConfigData[], selectedKey: string): string | null {
  for (const route of routes) {
    if (route.type === 'collapse' && route.children) {
      for (const child of route.children) {
        if (child.id === selectedKey) return route.id!;
      }
    }
    if (route.type === 'group' && route.children) {
      const found = findParentId(route.children, selectedKey);
      if (found) return found;
    }
  }
  return null;
}

type Props = {
  routesConfig: RouterConfigData[];
};

const AppVerticalNav: React.FC<Props> = ({ routesConfig }) => {
  const { menuStyle, sidebarColorSet } = useSidebarContext();

  const { pathname } = useLocation();
  // auth_code is cleared from URL on load and stored in Redux/localStorage,
  // so no need to propagate it through navigation links
  const filteredSearchStr = '';

  const { ids: menuIds, urlToId } = useMemo(() => collectMenuData(routesConfig), [routesConfig]);

  const selectedKeys = useMemo(() => {
    const path = pathname.substr(1);
    // Exact URL→ID match (e.g. path "timesheets" → id "timesheets/all")
    if (urlToId[path]) return urlToId[path];
    // Exact ID match
    if (menuIds.includes(path)) return path;
    // Explicit route→menu mappings for paths that don't follow prefix convention
    const routeToMenu: Record<string, string> = {
      'invoices/create-invoice-credit': 'invoices/credit-notes',
    };
    // Also match detail routes like invoices/credit-note/:id
    if (path.startsWith('invoices/credit-note/')) return 'invoices/credit-notes';
    if (routeToMenu[path]) return routeToMenu[path];

    // Longest prefix match
    let best = '';
    for (const id of menuIds) {
      if (path.startsWith(id + '/') || path.startsWith(id + '?')) {
        if (id.length > best.length) best = id;
      }
    }
    return best || path;
  }, [pathname, menuIds, urlToId]);

  // Auto-open parent collapse when on a sub-menu page
  const parentKey = useMemo(
    () => findParentId(routesConfig, selectedKeys),
    [routesConfig, selectedKeys],
  );

  const [openKeys, setOpenKeys] = useState<string[]>(parentKey ? [parentKey] : []);

  // Update openKeys when navigating to a sub-menu page
  useEffect(() => {
    if (parentKey && !openKeys.includes(parentKey)) {
      setOpenKeys((prev) => [...prev, parentKey]);
    }
  }, [parentKey]);

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find(
      (key: string) => openKeys.indexOf(key) === -1,
    );
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  const { messages } = useIntl();

  const { agencyUploadSummary } = useSelector(
    (state: AppState) => state.importStore,
  );

  const { countProspectPending, countDocumentIDPending } = useSelector(
    (state: AppState) => state.prospectStore,
  );

  const routesWithBadges = useMemo(() => {
    return routesConfig.map((route) => {
      // Import badges
      if (route.id === 'import') {
        const total = agencyUploadSummary?.total || 0;
        if (!total) return route;
        return {
          ...route,
          count: total,
          children: route.children?.map((child) => {
            if (child.id === 'import/timesheets')
              return { ...child, count: agencyUploadSummary?.totalTimeSheet || 0 };
            if (child.id === 'import/employees-subcontractors')
              return { ...child, count: agencyUploadSummary?.totalEmployee || 0 };
            if (child.id === 'import/prospects')
              return { ...child, count: agencyUploadSummary?.totalProspect || 0 };
            return child;
          }),
        };
      }
      // Prospect pending count badge
      if (route.id === 'prospects' && countProspectPending > 0) {
        return { ...route, count: countProspectPending };
      }
      // Document pending verification count badge
      if (route.id === 'documents' && countDocumentIDPending > 0) {
        return { ...route, count: countDocumentIDPending };
      }
      return route;
    });
  }, [routesConfig, agencyUploadSummary, countProspectPending, countDocumentIDPending]);

  return (
    <StyledVerticalNav
      theme={sidebarColorSet as any}
      color={sidebarColorSet?.sidebarMenuSelectedTextColor}
      mode="inline"
      className={clsx({
        'rounded-menu': menuStyle === MenuStyle.ROUNDED,
        'rounded-menu-reverse': menuStyle === MenuStyle.ROUNDED_REVERSE,
        'standard-menu': menuStyle === MenuStyle.STANDARD,
        'default-menu': menuStyle === MenuStyle.DEFAULT,
        'rounded-menu curved-menu': menuStyle === MenuStyle.CURVED_MENU,
        'menu-rounded': menuStyle === MenuStyle.ROUNDED,
        'menu-rounded rounded-menu-reverse':
          menuStyle === MenuStyle.ROUNDED_REVERSE,
        'menu-rounded standard-menu': menuStyle === MenuStyle.STANDARD,
        'menu-rounded curved-menu': menuStyle === MenuStyle.CURVED_MENU,
        'bg-color-menu':
          sidebarColorSet.sidebarBgColor !==
          defaultConfig.sidebar.colorSet.sidebarBgColor,
      })}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      selectedKeys={[selectedKeys]}
      items={getRouteMenus(routesWithBadges, messages, filteredSearchStr) as any}
    />
  );
};

export default AppVerticalNav;
